// Карточка спутника: раскрывается справа-внизу по клику на маркер, показывает
// паспорт КА (реестр) + орбиту/виток (пропагация TLE на выбранный момент). Поля
// приходят из SatelliteOrbit; card — «сырой» словарь, читаем мягко и недостающее
// честно показываем прочерком. Стиль — как VesselCard (панель Румб, тема).

import type { ReactNode } from 'react'
import type { SatelliteFeed, SatelliteOrbit } from '../api'
import { t, useT } from '../i18n'
import { categoryColor, categoryLabel } from './satLayer'
import { coverageRingLabel } from './orbitTrack'
import { feedLabel, type FeedState } from './feedLabel'
import { statusLabel } from './statusLabel'

interface SatelliteCardProps {
  data: SatelliteOrbit
  /** Поток позиций СОЗВЕЗДИЯ этого аппарата; null — привязать нечем. */
  feed: SatelliteFeed | null
  onClose: () => void
}

// Цвет точки состояния потока. Молчание — не ошибка кабинета, а факт о данных,
// поэтому янтарь/серый, а не аварийный красный.
const FEED_DOT: Record<FeedState, string> = {
  live: 'bg-sonar',
  lagging: 'bg-amber-400',
  silent: 'bg-mist',
  unattributed: 'bg-hull',
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Число с единицей и фиксированной точностью; null → прочерк. */
function fmtNum(v: number | null, unit: string, digits = 0): string {
  if (v == null) return '—'
  return `${v.toFixed(digits)} ${unit}`
}

// Человекочитаемая подпись происхождения записи (discovery_source). Неизвестный
// источник → null (бейдж не рисуем).
const DISCOVERY_SOURCE_KEY: Record<string, string> = {
  'celestrak-group': 'satellites.source.celestrak',
  satnogs: 'satellites.source.satnogs',
  individual: 'satellites.source.individual',
  manual: 'satellites.source.manual',
}

export function discoverySourceLabel(v: unknown): string | null {
  const code = str(v)
  const key = code != null ? DISCOVERY_SOURCE_KEY[code] : undefined
  return key ? t(key) : null
}

export function SatelliteCard({ data, feed, onClose }: SatelliteCardProps) {
  const tt = useT()
  const card = data.card
  const name = str(card.name) ?? tt('satellites.card.unnamed')
  const norad = num(card.norad_id)
  const category = str(card.category) ?? 'unknown'

  const period = num(data.orbit.period_min)
  const incl = num(data.orbit.inclination_deg)
  const apogee = num(data.orbit.apogee_km)
  const perigee = num(data.orbit.perigee_km)
  const orbitClass = str(data.orbit.orbit_class)

  const provenance = discoverySourceLabel(card.discovery_source)
  const status_ = feedLabel(feed)

  const cur = data.track.current
  const coverage =
    Number.isFinite(cur.footprint_radius_km) && Number.isFinite(cur.dead_zone_radius_km)
      ? coverageRingLabel(cur.footprint_radius_km, cur.dead_zone_radius_km)
      : '—'

  return (
    <div className="console-scroll pointer-events-auto absolute bottom-4 right-4 z-10 max-h-[calc(100%-2rem)] w-72 overflow-y-auto rounded-xl border border-hull bg-deep p-3 shadow-console">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foam" title={name}>
            {name}
          </div>
          <div className="tnum text-[11px] text-mist">
            NORAD {norad != null ? norad : (str(card.norad_id) ?? '—')}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={tt('common.close')}
          className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-mist transition-colors hover:bg-haze hover:text-foam"
        >
          ×
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2 text-[12px]">
        <span
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: categoryColor(category) }}
          aria-hidden
        />
        <span className="truncate text-foam">{categoryLabel(category)}</span>
        {provenance && (
          <span
            className="ml-auto shrink-0 rounded-full border border-hull px-1.5 py-0.5 text-[10px] leading-none text-mist"
            title={tt('satellites.card.provenance')}
          >
            {provenance}
          </span>
        )}
      </div>

      <dl className="mt-2 space-y-1 border-t border-hull-hair pt-2 text-[12px]">
        <Row label={tt('satellites.card.operator')} value={str(card.operator) ?? '—'} />
        <Row label={tt('satellites.card.legalEntity')} value={str(card.legal_entity) ?? '—'} />
        <Row label={tt('satellites.card.country')} value={str(card.country) ?? '—'} />
        <Row label={tt('satellites.card.status')} value={statusLabel(str(card.status))} />
        <Row label={tt('satellites.card.objectType')} value={str(card.object_type) ?? '—'} />
        <Row label={tt('satellites.card.orbitClass')} value={orbitClass ?? '—'} />
        <Row label={tt('satellites.card.period')} value={fmtNum(period, tt('satellites.unit.min'), 1)} />
        <Row label={tt('satellites.card.inclination')} value={incl != null ? `${incl.toFixed(1)}°` : '—'} />
        <Row
          label={tt('satellites.card.apogeePerigee')}
          value={`${fmtNum(apogee, tt('satellites.unit.km'))} · ${fmtNum(perigee, tt('satellites.unit.km'))}`}
        />
        <Row label={tt('satellites.card.coverage')} value={coverage} />
        <Row label={tt('satellites.card.launch')} value={str(card.launch_date) ?? '—'} />
        <Row label={tt('satellites.card.tleEpoch')} value={str(card.tle_epoch) ?? '—'} />
      </dl>

      {/* Поток позиций — единственное прямое свидетельство, что приёмник работает:
          каталожный «Статус» выше говорит лишь о факте орбиты. Признак считается
          ПО СОЗВЕЗДИЮ: поставщики не раскрывают спутник-источник позиции. */}
      <div className="mt-2 border-t border-hull-hair pt-2 text-[12px]">
        <div className="text-faint">{tt('satellites.card.feed')}</div>
        <div className="mt-1 flex items-start gap-1.5">
          <span
            aria-hidden
            className={`mt-1 h-2 w-2 shrink-0 rounded-full ${FEED_DOT[status_.state]}`}
          />
          <span className="min-w-0 text-foam">{status_.text}</span>
        </div>
        {status_.volume && <div className="tnum mt-0.5 text-mist">{status_.volume}</div>}
        <div className="mt-1 text-[11px] leading-snug text-faint">
          {tt('satellites.card.feedDisclaimer')}
        </div>
      </div>
    </div>
  )
}

function Row({ label, value, children }: { label: string; value?: string; children?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="tnum min-w-0 truncate text-right font-medium text-foam">{children ?? value}</dd>
    </div>
  )
}
